import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import type { Reference } from "@apollo/client";
import { gql } from "graphql-tag";
import { toast } from "sonner";
import type { MessageFact } from "../components/SourceReview";
import type { FactSourceRef } from "../lib/factSourceReferenceIds";

export const ADD_FACT_MUTATION = gql`
  mutation AddFact($messageId: UUID!, $content: String!, $customerRequestId: UUID) {
    messageAddFact(messageId: $messageId, content: $content, customerRequestId: $customerRequestId)
  }
`;

export const REMOVE_FACT_MUTATION = gql`
  mutation RemoveFact($factId: UUID!) {
    messageRemoveFact(factId: $factId)
  }
`;

export const EDIT_FACT_MUTATION = gql`
  mutation EditFact($factId: UUID!, $content: String!) {
    messageEditFact(factId: $factId, content: $content)
  }
`;

const NEW_FACT_FRAGMENT = gql`
  fragment NewFactInline on MessageFactSchema {
    id
    content
    createdAt
    deletedAt
    customerRequestId
    isAiGenerated
  }
`;

export interface MessageFactSnapshot {
  id: string;
  content: string;
  createdAt: string;
  deletedAt?: string | null;
  /** Optional to-do scope. null/undefined = a general per-message fact; a value
   *  scopes the fact to that to-do's card (the to-do's `id` / rag_question_id). */
  customerRequestId?: string | null;
  /** True for an AI-prefilled bullet (rendered italic until the agent edits it). */
  isAiGenerated?: boolean;
  /** Grounding sources of an AI-prefilled bullet (#500). Passed straight through
   *  to the rendered fact so hovering the row can highlight those source cards.
   *  Arrives as an untyped JSON scalar from GraphQL; the resolver
   *  (`factSourceReferenceIds`) reads only the identity fields it needs. */
  sourceRefs?: readonly FactSourceRef[] | null;
}

interface UseFactsNotebookOptions {
  messageId: string | null | undefined;
  /** The message's messageFacts as seen by the consumer. Whether this
   *  is a live Apollo query result (Venus) or a frozen snapshot (Neptune) is
   *  irrelevant — the hook layers optimistic patches on top and reconciles
   *  whenever this prop changes. */
  serverFacts: readonly MessageFactSnapshot[] | null | undefined;
  /** Fires once after every successful add / remove / edit. Neptune wires
   *  this to its message refetch so its sessionStorage snapshot catches up.
   *  Venus omits it — its useQuery already subscribes to the cache.modify
   *  we do internally. */
  onMutationCompleted?: () => void;
  /** Fires once after every successful add / remove / edit. Consumers flip
   *  the "regenerate" CTA flag here. */
  onChange?: () => void;
}

export interface AddDraftHandle {
  /** Local id under which the optimistic patch is tracked. Stable until the
   *  server responds. Use this id to follow up with handleEdit / handleRemove
   *  before the realId is known. */
  tempId: string;
  /** Resolves once the mutation settles. realId is the server-assigned id on
   *  success, null on failure or empty server response (patch already rolled
   *  back in that case). */
  settled: Promise<{ realId: string | null }>;
}

interface UseFactsNotebookReturn {
  /** Optimistic-merged facts ready to feed into <FactsNotebook />. Each
   *  fact carries its `customerRequestId` (null = general per-message fact). Consumers can
   *  filter this directly or use {@link factsFor} for a scoped slice. */
  facts: MessageFact[];
  /** Returns only the facts belonging to one scope: pass `null` for the general
   *  workspace-level facts ("Meine Fakten"), or a to-do's `id` for that to-do's
   *  per-card facts. A thin selector over {@link facts} — it does NOT spin up a
   *  second hook instance or cache writer, so every scope shares the one
   *  serialized optimistic/cache path (no cross-field races). */
  factsFor: (customerRequestId: string | null) => MessageFact[];
  /** Loading flag for the add mutation, exposed for the notebook's isAdding prop. */
  isAdding: boolean;
  /** Adds an optimistic patch synchronously and returns the tempId immediately.
   *  Subsequent handleEdit / handleRemove calls may use either tempId or realId
   *  to refer to the same fact — the hook resolves both. `customerRequestId` scopes the new
   *  fact to one to-do (omit / null for a general per-message fact). */
  handleAdd: (content: string, customerRequestId?: string | null) => AddDraftHandle;
  /** Like handleAdd, but the new row is rendered directly after the fact
   *  identified by `afterId` (which may be a tempId or realId of any visible
   *  entry — server fact, prior pending add, or anything in between). If the
   *  anchor disappears before the patch resolves, the row falls back to
   *  appending at the end of the list. `customerRequestId` scopes the new fact (omit / null
   *  for a general per-message fact). */
  handleAddAfter: (
    afterId: string,
    content: string,
    customerRequestId?: string | null,
  ) => AddDraftHandle;
  handleRemove: (factId: string) => void;
  handleEdit: (factId: string, content: string) => Promise<void>;
}

interface PendingAdd {
  tempId: string;
  realId?: string;
  /** What we currently display for this row. The caller may update this via
   *  handleEdit while the add is still in flight (no realId yet) — we record
   *  the new content locally without firing a mutation. */
  content: string;
  /** What was actually sent in the server's add mutation. If the user kept
   *  typing while the add was in flight, `content` drifts past
   *  `originalContent`; once the realId arrives we flush an edit so the
   *  server catches up. */
  originalContent: string;
  createdAt: string;
  /** When set, the row should render directly after the entry whose
   *  tempId/realId equals this value. Updated automatically when an anchor
   *  pending add promotes its tempId to a realId so positional intent
   *  survives the temp→real transition. */
  afterId?: string;
  /** The to-do this pending fact is scoped to (null = a general per-message
   *  fact). Sent in the add mutation variables and carried through the merge so
   *  the optimistic row is attributed to the right scope before the server
   *  echoes its own `customerRequestId` back. */
  customerRequestId: string | null;
}

/** Tempid prefix — every locally-generated draft id starts with this. The
 *  hook's handlers refuse to forward ids with this prefix to the server. */
const TEMP_ID_PREFIX = "pending-";

/**
 * Owns the add / remove / edit mutations for the agent-fact notebook and
 * keeps the UI feeling instant under any network conditions.
 *
 * Two layers run in parallel:
 *
 *   1. Apollo cache writes inside each mutation's `update()` — these keep
 *      the normalized cache in sync so any consumer that subscribes via
 *      `useQuery` / `useFragment` sees the new fact synchronously.
 *
 *   2. Local optimistic patches (`pendingAdds`, `pendingRemoves`,
 *      `pendingEdits`) layered on top of `serverFacts` — these cover
 *      consumers that hold a frozen snapshot (e.g. Neptune's
 *      sessionStorage-backed `useMessageCache`) where the cache.modify
 *      never propagates back to the snapshot until a refetch lands.
 *
 * Patches self-clear once `serverFacts` reflects the same change — the
 * cleanup `useEffect` compares each patch against the new server view.
 * Patches also reset whenever `messageId` changes so dismissals never
 * cross message boundaries.
 *
 * SINGLE-OWNER INVARIANT: there is exactly ONE instance of this hook per
 * message, and it is the sole writer of the agent-fact optimistic state and of
 * the `Message.messageFacts` Apollo cache field. Facts scoped to
 * different to-dos are NOT served by separate hook instances — that would race
 * N writers on the same cache field (duplicate-row flashes, lost adds). Instead
 * every fact carries a `customerRequestId` (null = general); the single instance tags each
 * pending/real fact with its scope and exposes scoped reads via `factsFor`,
 * while all add/remove/edit mutations stay on one serialized path. Hosts MUST
 * thread the scoped data + handlers down to per-to-do sections rather than
 * mounting a second instance.
 */
export function useFactsNotebook({
  messageId,
  serverFacts,
  onMutationCompleted,
  onChange,
}: UseFactsNotebookOptions): UseFactsNotebookReturn {
  const [pendingAdds, setPendingAdds] = useState<PendingAdd[]>([]);
  const [pendingRemoves, setPendingRemoves] = useState<Set<string>>(
    () => new Set(),
  );
  const [pendingEdits, setPendingEdits] = useState<Map<string, string>>(
    () => new Map(),
  );
  // Maps a server-confirmed realId back to the tempId we minted for it.
  // Lets the merged facts list keep the SAME stableKey across the entire
  // lifecycle of a row (pending → confirmed → cleaned up). Without this,
  // React would unmount the row at the temp→real flip (losing the textarea
  // focus mid-edit), and an in-flight `insertAfterTarget` referencing the
  // tempId would silently stop matching the now-realId row.
  const [tempByReal, setTempByReal] = useState<Map<string, string>>(
    () => new Map(),
  );
  // Mirror pendingAdds in a ref so async callbacks (like handleAdd's settled
  // handler) can read the LATEST patch state without relying on React's
  // update-function timing, which is fragile in StrictMode / concurrent
  // rendering.
  const pendingAddsRef = useRef(pendingAdds);
  useEffect(() => {
    pendingAddsRef.current = pendingAdds;
  }, [pendingAdds]);

  const prevMessageId = useRef(messageId);
  useEffect(() => {
    if (messageId !== prevMessageId.current) {
      prevMessageId.current = messageId;
      setPendingAdds([]);
      setPendingRemoves(new Set());
      setPendingEdits(new Map());
      setTempByReal(new Map());
    }
  }, [messageId]);

  // Cleanup pass for pendingRemoves / pendingEdits only. pendingAdds is
  // intentionally NOT pruned here — keeping each pendingAdd around past
  // the server's first echo is what lets the merged list preserve the
  // user-intended position of a row. Without that, the server's
  // chronological order would yank a freshly-inserted fact to the end of
  // the list the moment the realId arrived (visible jump while the agent
  // is still typing nearby). The pendingAdd's content is sourced from the
  // server fact once available, so it stays in sync with later edits and
  // remote deletes.
  useEffect(() => {
    if (!serverFacts) return;
    const byId = new Map(serverFacts.map((f) => [f.id, f] as const));
    setPendingRemoves((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of prev) {
        const f = byId.get(id);
        if (!f || f.deletedAt) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    setPendingEdits((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const [id, content] of prev) {
        const f = byId.get(id);
        if (f && f.content === content) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // Cleanup for confirmed pendingAdds. Three cases:
    //
    //   1. Server still reports the row alive → keep. The pendingAdd is the
    //      positional source of truth; content flows through from the server
    //      fact via the merge.
    //   2. Server marks the row deletedAt (another tab removed it) → drop.
    //   3. Server doesn't report the row at all — could be the user just
    //      deleted it in this session, OR a polling refetch fired BEFORE
    //      the server propagated our new add to its read path. Drop ONLY
    //      when the local pendingRemoves shows the user explicitly removed
    //      it; otherwise hold on so the row doesn't jump to the
    //      chronological end mid-typing while the refetch reconciles.
    //
    // In-flight adds (no realId yet) are always left alone.
    setPendingAdds((prev) => {
      let changed = false;
      const next = prev.filter((a) => {
        if (!a.realId) return true;
        const sf = byId.get(a.realId);
        if (sf && !sf.deletedAt) return true;
        if (sf && sf.deletedAt) {
          changed = true;
          return false;
        }
        // sf missing — only drop if the user explicitly removed it locally.
        if (pendingRemoves.has(a.realId)) {
          changed = true;
          return false;
        }
        return true;
      });
      return changed ? next : prev;
    });
  }, [serverFacts]);

  const facts: MessageFact[] = useMemo(() => {
    // pendingAdds is the user-authored backbone of the list — it preserves
    // the order the agent typed facts in, including any insert-after
    // splices, even after the server confirms each one. Each pending entry
    // produces ONE rendered row; content prefers the server fact (when
    // echoed) so remote edits and pendingEdits flow through.
    const serverById = new Map(
      (serverFacts ?? [])
        .filter((f) => !f.deletedAt && !pendingRemoves.has(f.id))
        .map((f) => [f.id, f] as const),
    );

    // Which server rows did a pendingAdd already account for? Those should
    // NOT also appear in the trailing "server-only" section below — that
    // would duplicate the row in the visible list.
    const consumedServerIds = new Set<string>();

    const pendingEntries: Array<{ entry: MessageFact; afterId?: string }> =
      pendingAdds
        .filter((a) => !pendingRemoves.has(a.tempId))
        .filter((a) => !a.realId || !pendingRemoves.has(a.realId))
        .map((a) => {
          const sf = a.realId ? serverById.get(a.realId) : undefined;
          if (sf) consumedServerIds.add(sf.id);
          const sourceContent = sf
            ? (pendingEdits.get(sf.id) ?? sf.content)
            : a.content;
          return {
            entry: {
              id: a.realId ?? a.tempId,
              content: sourceContent,
              // Prefer the server's authoritative createdAt once available
              // so the displayed timestamp matches what other clients see.
              createdAt: sf?.createdAt ?? a.createdAt,
              // Scope: prefer the server's echoed customerRequestId once available, else the
              // customerRequestId the pending add was tagged with at insertion time.
              customerRequestId: sf?.customerRequestId ?? a.customerRequestId,
              // A pending add is always human-typed; once it reconciles to a
              // server fact prefer the server's flag (which an edit may flip).
              isAiGenerated: sf?.isAiGenerated ?? false,
              // Provenance only ever comes from the server (#500) — a locally
              // typed fact has none until (and unless) the echoed row carries it.
              sourceRefs: sf?.sourceRefs ?? null,
              stableKey: a.tempId,
            },
            afterId: a.afterId,
          };
        });

    // Server rows that no pendingAdd accounted for — facts created in a
    // different session (or by another agent). They land in chronological
    // order at the end of the list, after all user-positioned rows.
    const serverOnly: MessageFact[] = [...serverById.values()]
      .filter((f) => !consumedServerIds.has(f.id))
      .map((f) => ({
        id: f.id,
        content: pendingEdits.get(f.id) ?? f.content,
        createdAt: f.createdAt,
        customerRequestId: f.customerRequestId ?? null,
        // A staged local edit means the agent has adopted an AI bullet — drop
        // the italic immediately, matching the server flip edit_agent_fact does.
        isAiGenerated: pendingEdits.has(f.id) ? false : (f.isAiGenerated ?? false),
        // Provenance survives an edit (the backend flips is_ai_generated but keeps
        // source_refs), so the hover highlight keeps working on adopted bullets —
        // which is when the agent is most likely to be checking the source (#500).
        sourceRefs: f.sourceRefs ?? null,
        stableKey: tempByReal.get(f.id) ?? f.id,
      }));

    // Splice anchored pendings directly after their target. Iterate in
    // pendingAdds insertion order so chained anchors (T2 anchored on T1)
    // find T1 already placed in `result`. Match anchor by stableKey OR id
    // — either is a valid reference at the moment of the original Enter.
    const result: MessageFact[] = [...serverOnly];
    const unanchored: MessageFact[] = [];
    for (const p of pendingEntries) {
      if (!p.afterId) {
        unanchored.push(p.entry);
        continue;
      }
      const idx = result.findIndex(
        (f) => f.stableKey === p.afterId || f.id === p.afterId,
      );
      if (idx === -1) {
        // Anchor disappeared (target removed, or id ref drifted past a
        // stale render) — fall back to append so the row stays visible
        // somewhere.
        unanchored.push(p.entry);
      } else {
        result.splice(idx + 1, 0, p.entry);
      }
    }
    return [...result, ...unanchored];
  }, [serverFacts, pendingAdds, pendingRemoves, pendingEdits, tempByReal]);

  // Scoped read: a thin filter over the single merged `facts` list. No extra
  // cache writer or hook instance — every scope shares the one serialized
  // optimistic/cache path, which is what keeps multi-scope reads race-free.
  const factsFor = useCallback(
    (customerRequestId: string | null): MessageFact[] =>
      facts.filter((f) => (f.customerRequestId ?? null) === customerRequestId),
    [facts],
  );

  const [addFact, { loading: isAdding }] = useMutation<
    { messageAddFact: string },
    { messageId: string; content: string; customerRequestId?: string | null }
  >(ADD_FACT_MUTATION, {
    // No optimisticResponse: an Apollo-side optimistic write would create a
    // second row at the temp id Apollo generates, while our local patches
    // already have the row at OUR tempId. Two ids → duplicate row flash.
    // The local patches alone drive the instant UI; the cache.modify below
    // syncs Apollo on real-response success so live consumers' useQuery
    // results converge.
    update(cache, result, options) {
      const newId = result.data?.messageAddFact;
      const mid = options.variables?.messageId;
      const content = options.variables?.content;
      if (!newId || !mid || !content) return;
      // The to-do scope the fact was added under (null = general). Mirrored into
      // the cache fragment so live consumers' useQuery reads see the right scope
      // immediately, matching the server's persisted customerRequest_id.
      const customerRequestId = options.variables?.customerRequestId ?? null;
      const messageCacheId = cache.identify({
        __typename: "Message",
        id: String(mid),
      });
      if (!messageCacheId) return;
      const newFactRef = cache.writeFragment({
        data: {
          __typename: "MessageFactSchema",
          id: String(newId),
          content,
          createdAt: new Date().toISOString(),
          deletedAt: null,
          customerRequestId,
          // A fact added through this mutation is always human-authored.
          isAiGenerated: false,
        },
        fragment: NEW_FACT_FRAGMENT,
      });
      if (!newFactRef) return;
      cache.modify({
        id: messageCacheId,
        fields: {
          messageFacts(existing: readonly Reference[] = []) {
            return [...existing, newFactRef];
          },
        },
      });
    },
    onCompleted: () => {
      onChange?.();
      onMutationCompleted?.();
    },
    onError: (error) => {
      toast.error(`Fakt konnte nicht gespeichert werden: ${error.message}`);
      console.error("messageAddFact failed", error);
    },
  });

  const [removeFact] = useMutation<
    { messageRemoveFact: boolean },
    { factId: string }
  >(REMOVE_FACT_MUTATION, {
    // No optimisticResponse: local patch handles the instant UI; cache.modify
    // on real-response keeps live consumers' useQuery results consistent.
    update(cache, _result, options) {
      const factId = options.variables?.factId;
      if (!factId) return;
      const factCacheId = cache.identify({
        __typename: "MessageFactSchema",
        id: String(factId),
      });
      if (!factCacheId) return;
      cache.modify({
        id: factCacheId,
        fields: {
          deletedAt: () => new Date().toISOString(),
        },
      });
    },
    onCompleted: () => {
      onChange?.();
      onMutationCompleted?.();
    },
    onError: (error) => {
      toast.error(`Fakt konnte nicht entfernt werden: ${error.message}`);
      console.error("messageRemoveFact failed", error);
    },
  });

  const [editFact] = useMutation<
    { messageEditFact: boolean },
    { factId: string; content: string }
  >(EDIT_FACT_MUTATION, {
    // No optimisticResponse: local patch handles the instant UI; cache.modify
    // on real-response keeps live consumers' useQuery results consistent.
    update(cache, _result, options) {
      const factId = options.variables?.factId;
      const content = options.variables?.content;
      if (!factId || !content) return;
      const factCacheId = cache.identify({
        __typename: "MessageFactSchema",
        id: String(factId),
      });
      if (!factCacheId) return;
      cache.modify({
        id: factCacheId,
        fields: {
          content: () => content,
        },
      });
    },
    onCompleted: () => {
      onChange?.();
      onMutationCompleted?.();
    },
    onError: (error) => {
      toast.error(`Fakt konnte nicht gespeichert werden: ${error.message}`);
      console.error("messageEditFact failed", error);
    },
  });

  const doAdd = useCallback(
    (
      content: string,
      afterId: string | undefined,
      customerRequestId: string | null,
    ): AddDraftHandle => {
      if (!messageId) {
        // No message in scope — return a no-op handle so callers can still
        // chain without null-checking.
        return { tempId: "", settled: Promise.resolve({ realId: null }) };
      }
      const tempId = `${TEMP_ID_PREFIX}${messageId}-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      setPendingAdds((prev) => [
        ...prev,
        {
          tempId,
          content,
          originalContent: content,
          createdAt: new Date().toISOString(),
          afterId,
          customerRequestId,
        },
      ]);
      const settled: Promise<{ realId: string | null }> = addFact({
        variables: { messageId, content, customerRequestId },
      })
        .then((result) => {
          const realId = result.data?.messageAddFact;
          if (!realId) {
            // Server returned without an id — drop the patch.
            setPendingAdds((prev) => prev.filter((a) => a.tempId !== tempId));
            return { realId: null };
          }
          const realIdStr = String(realId);

          // The caller may have updated the patch's content via handleEdit
          // while this add was in flight. Detect that drift here and flush
          // a follow-up edit so the server reflects the user's latest text.
          const currentPatch = pendingAddsRef.current.find(
            (a) => a.tempId === tempId,
          );
          const driftedContent =
            currentPatch &&
            currentPatch.content !== currentPatch.originalContent
              ? currentPatch.content
              : null;
          // Record the temp→real alias BEFORE the pending cleanup so the
          // facts useMemo's stableKey lookup finds the original tempId
          // even after the row migrates from pendingAdds to serverFacts.
          setTempByReal((prev) => {
            if (prev.get(realIdStr) === tempId) return prev;
            const next = new Map(prev);
            next.set(realIdStr, tempId);
            return next;
          });
          setPendingAdds((prev) =>
            prev.map((a) => {
              if (a.tempId === tempId) {
                return {
                  ...a,
                  realId: realIdStr,
                  // Mark originalContent in sync with what we're about to
                  // send so we don't re-flush if cleanup is delayed.
                  originalContent: driftedContent ?? a.originalContent,
                };
              }
              return a;
            }),
          );
          if (driftedContent !== null) {
            // Stage the drifted content in pendingEdits so the merged facts
            // list keeps showing the user's latest text WHILE the edit
            // mutation is in flight. Without this, the merge briefly falls
            // back to the server's stale content (the value the add was
            // first sent with) and the row flashes to the old text until
            // the edit response lands.
            setPendingEdits((prev) => {
              const next = new Map(prev);
              next.set(realIdStr, driftedContent);
              return next;
            });
            void editFact({
              variables: { factId: realIdStr, content: driftedContent },
            }).catch(() => {
              // onError already toasted by the edit mutation's handler. Drop
              // the staged pendingEdit so the row reverts to whatever the
              // server actually has.
              setPendingEdits((prev) => {
                if (!prev.has(realIdStr)) return prev;
                const next = new Map(prev);
                next.delete(realIdStr);
                return next;
              });
            });
          }
          return { realId: realIdStr };
        })
        .catch(() => {
          // onError already toasted. Roll back the optimistic patch.
          setPendingAdds((prev) => prev.filter((a) => a.tempId !== tempId));
          return { realId: null };
        });
      return { tempId, settled };
    },
    [addFact, editFact, messageId],
  );

  const handleAdd = useCallback(
    (content: string, customerRequestId: string | null = null): AddDraftHandle =>
      doAdd(content, undefined, customerRequestId),
    [doAdd],
  );

  const handleAddAfter = useCallback(
    (
      afterId: string,
      content: string,
      customerRequestId: string | null = null,
    ): AddDraftHandle => doAdd(content, afterId, customerRequestId),
    [doAdd],
  );

  const handleRemove = useCallback(
    (factId: string) => {
      const pendingAdd = pendingAdds.find(
        (a) => a.tempId === factId || a.realId === factId,
      );
      if (pendingAdd && !pendingAdd.realId) {
        // Still pending server confirm — drop the patch locally; the in-flight
        // add will resolve into a no-op cleanup.
        setPendingAdds((prev) =>
          prev.filter((a) => a.tempId !== pendingAdd.tempId),
        );
        return;
      }
      // For a pendingAdd that has a realId, fire the mutation with the realId
      // (the caller may still hold the original tempId).
      const targetId = pendingAdd?.realId ?? factId;
      // Defensive guard: a temp id should never reach the server. This can
      // happen if the patch was cleaned up before the caller's id ref caught
      // up to the realId. Silently drop — the server has the truth.
      if (targetId.startsWith(TEMP_ID_PREFIX)) return;
      setPendingRemoves((prev) => {
        if (prev.has(targetId)) return prev;
        const next = new Set(prev);
        next.add(targetId);
        return next;
      });
      removeFact({ variables: { factId: targetId } }).catch(() => {
        setPendingRemoves((prev) => {
          if (!prev.has(targetId)) return prev;
          const next = new Set(prev);
          next.delete(targetId);
          return next;
        });
      });
    },
    [removeFact, pendingAdds],
  );

  const handleEdit = useCallback(
    async (factId: string, content: string) => {
      const pendingAdd = pendingAdds.find(
        (a) => a.tempId === factId || a.realId === factId,
      );
      if (pendingAdd && !pendingAdd.realId) {
        // Still pending — update the patch locally only. handleAdd's settled
        // handler will detect the drift when the realId arrives and flush an
        // edit to the server.
        setPendingAdds((prev) =>
          prev.map((a) =>
            a.tempId === pendingAdd.tempId ? { ...a, content } : a,
          ),
        );
        return;
      }
      const targetId = pendingAdd?.realId ?? factId;
      // Defensive guard: never send a temp id to the server. If the patch
      // was cleaned up before the caller's id ref caught up to realId, the
      // server-side content is already the latest (drift was flushed by
      // handleAdd). Drop silently.
      if (targetId.startsWith(TEMP_ID_PREFIX)) return;
      setPendingEdits((prev) => {
        const next = new Map(prev);
        next.set(targetId, content);
        return next;
      });
      try {
        await editFact({ variables: { factId: targetId, content } });
      } catch {
        setPendingEdits((prev) => {
          if (!prev.has(targetId)) return prev;
          const next = new Map(prev);
          next.delete(targetId);
          return next;
        });
      }
    },
    [editFact, pendingAdds],
  );

  return {
    facts,
    factsFor,
    isAdding,
    handleAdd,
    handleAddAfter,
    handleRemove,
    handleEdit,
  };
}
